import React, { useEffect, useRef, useState } from 'react';
import { useArc } from '../context/ArcContext';
import { Sparkles, Zap, Trash2, Check, Lock, Loader2, Play, CheckCircle2, BookOpen } from 'lucide-react';
import { assertBackendUrl } from '../lib/backendUrl';

const API = assertBackendUrl();

function nowIso() { return new Date().toISOString(); }

function addHistory(item, type, extra = {}) {
  return {
    ...item,
    history: [...(item.history || []), { type, at: nowIso(), ...extra }],
  };
}

function createTask(text, goalId) {
  return {
    id: genId(),
    text,
    done: false,
    startedAt: null,
    completedAt: null,
    goalId: goalId || null,
    history: [{ type: 'created', at: nowIso() }],
  };
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function genId() { return Math.random().toString(36).slice(2, 10); }

function truncate(s, n) { return s.length > n ? s.slice(0, n - 1) + '…' : s; }

function inferTopic(text) {
  const t = text.toLowerCase();
  if (/(boat|yacht|sail|sailboat)/.test(t)) return 'boat';
  if (/(company|business|startup|agency|saas|firm)/.test(t)) return 'business';
  if (/(income|financial freedom|retire|wealth|passive)/.test(t)) return 'income';
  if (/(fitness|run|marathon|strength|muscle|lose weight|gym)/.test(t)) return 'fitness';
  if (/(house|property|real estate|apartment|land|home)/.test(t)) return 'property';
  if (/(book|writer|artist|musician|design|paint|album)/.test(t)) return 'craft';
  if (/(travel|world|nomad|move country|trip)/.test(t)) return 'travel';
  if (/(language|learn|skill|degree|study|course)/.test(t)) return 'learning';
  return 'generic';
}

function computeStatus(task) {
  if (task.done) return 'done';
  if (!task.startedAt) return 'not_started';
  const days = (Date.now() - task.startedAt) / 86400000;
  if (days > 90) return 'overdue';
  if (days > 30) return 'high_priority';
  return 'ongoing';
}

function getGoalTasks(goal) {
  return (goal?.layers || []).flatMap(layer => layer.tasks || []);
}

function isGoalReadyToComplete(goal) {
  const tasks = getGoalTasks(goal);
  return tasks.length > 0 && tasks.every(task => task.done);
}

// ─── Conversation data ────────────────────────────────────────────────────────

const Q1_SUGGESTIONS = {
  boat:     ['Freedom on open water, no schedule', 'A shared experience with family', 'A personal challenge worth training for', 'Upgrading how I spend my weekends'],
  business: ['Time and location on my own terms', 'Financial independence without a ceiling', 'Build something that outlasts me', 'Prove it\'s possible from where I\'m starting'],
  income:   ['Replace my salary with something I own', 'Cover my costs without showing up anywhere', 'Security for my family without the grind', 'Real independence where money is not the limit'],
  fitness:  ['Prove something I\'ve been avoiding', 'Feel capable again in my own body', 'Set the example my family needs', 'A milestone I can point to for life'],
  property: ['A stable asset that works while I sleep', 'Put capital somewhere it won\'t erode', 'Build a portfolio, not just a home', 'Control over where I live and what I own'],
  craft:    ['Leave something with my name on it', 'Master what I\'ve been circling for years', 'Make a living doing the only thing I want', 'Create something that resonates with people'],
  travel:   ['Permanent flexibility with no fixed location', 'See the world while I still can', 'A reset that changes everything', 'Freedom from a calendar that owns me'],
  learning: ['Open a door currently closed to me', 'Close the gap between where I am and need to be', 'Get the skill that changes my options', 'Build something with it immediately'],
  generic:  ['Personal freedom I don\'t have right now', 'Financial security that removes pressure', 'A goal that gives my work meaning', 'Something I\'ll be proud of in ten years'],
};

const Q3_SUGGESTIONS = {
  boat:     ['I don\'t have the capital yet', 'I lack the sailing skill', 'Time to maintain and use it', 'I haven\'t found the right vessel'],
  business: ['I don\'t have a paying client yet', 'Building while employed full-time', 'I need more capital or runway', 'Haven\'t nailed what I\'m actually selling'],
  income:   ['My current income is too unpredictable', 'I haven\'t productised anything yet', 'I don\'t know where to start', 'I have the idea but not the system'],
  fitness:  ['Consistency. I start and stop', 'I need the right coach or programme', 'Injury or a physical limit', 'Time, genuinely not just an excuse'],
  generic:  ['Money or capital', 'Time and bandwidth', 'The skill or knowledge gap', 'The right connections or environment'],
};

const Q4_SUGGESTIONS = {
  boat:     ['A specific boat and port by a specific date', 'Enough water-time monthly to actually improve', 'A crossing I can name in one sentence', 'Own it outright with no finance on it'],
  business: ['A specific MRR by a date I\'m committed to', 'Replaced employment with income fully covered', 'A team that runs it without me', 'An asset I can value or exit'],
  generic:  ['A date and number I\'m committed to', 'Something I can show someone who\'ll hold me to it', 'A change in how I live every single week', 'Something I\'ll know when I hit it'],
};

const CONV_QUESTIONS = [
  { q: () => 'Why do you actually want this?',                     s: (topic) => Q1_SUGGESTIONS[topic] || Q1_SUGGESTIONS.generic },
  { q: () => 'What makes right now the moment, not in 2 years?', s: () => ['Something in my life just shifted', 'I\'ve already waited too long', 'The window is closing and I can feel it', 'I\'m finally in a position to actually do it'] },
  { q: () => 'What\'s genuinely in your way today?',              s: (topic) => Q3_SUGGESTIONS[topic] || Q3_SUGGESTIONS.generic },
  { q: () => 'What does success look like, specifically?',       s: (topic) => Q4_SUGGESTIONS[topic] || Q4_SUGGESTIONS.generic },
  { q: () => 'What do you bring that most people wouldn\'t?',     s: () => ['A track record in something adjacent', 'More time or bandwidth than most at this stage', 'Connections that cut the usual path', 'Tolerance for the slow burn. I outlast most'] },
];

// ─── Topic plans ──────────────────────────────────────────────────────────────

const TOPIC_PLANS = {
  boat: {
    layers: [
      { name: 'Discover & Reality-Check', tasks: [
        'List the 3 specific use cases (day-sail, weekending, family trips, racing).',
        'Cost a realistic option and a stretch option, include slip, insurance, winter storage.',
        'Talk to 3 current owners. Ask each: "what would you have done differently?"',
        'Decide the entry path: club / charter / fractional / own.',
      ] },
      { name: 'Build the Capability', tasks: [
        'Enroll in a basic certification (ICC / RYA / ASA, or local equivalent).',
        'Log 20 hours on the water with a qualified skipper before any purchase.',
        'Complete a basic VHF radio + safety-at-sea course.',
        'Pass an open-water competence assessment.',
      ] },
      { name: 'Acquire & Operate', tasks: [
        'Confirm budget: cash + finance ratio you actually accept.',
        'Build a 12-month cost model (fixed + variable). Pressure-test it.',
        'Inspect 5 candidate boats. Get one independent surveyor report.',
        'Negotiate on documented findings, not the listing price. Close the deal.',
      ] },
    ],
  },
  business: {
    layers: [
      { name: 'Validate the Idea', tasks: [
        'Write the problem and the solution in one line each.',
        'Talk to 20 prospective customers. Capture words verbatim.',
        'Ship a minimum offer with a real price. Land 3 paying customers in 30 days.',
        'Decide: pivot, persist, or kill.',
      ] },
      { name: 'Build the Engine', tasks: [
        'Pick one channel where your customer already gathers.',
        'Publish or outreach 5×/week for 60 days. Measure reply rate.',
        'Build one referral mechanic that costs nothing to operate.',
        'Document the channel, turn it into a manual.',
      ] },
      { name: 'Compound', tasks: [
        'Hire one role owner to remove yourself from the lowest-leverage task.',
        'Pick one metric. Double it. Then double it again.',
        'Install pricing that scales with value, not hours.',
        'Reinvest 60% of surplus into the next install.',
      ] },
    ],
  },
  income: {
    layers: [
      { name: 'Map the Floor', tasks: [
        'Calculate your true monthly foundation (essentials only).',
        'List your current income lines and rate each on stability.',
        'Identify the gap between foundation and current passive income.',
        'Decide: which one income line do you install next?',
      ] },
      { name: 'Install the Line', tasks: [
        'Pick one income line you can install in 90 days.',
        'Dedicate 2 focused hours/week for 12 weeks. Track honestly.',
        'Cover 25% of foundation with the new line. Then 50%, then 100%.',
        'Reinvest the surplus into the next install.',
      ] },
      { name: 'Compound', tasks: [
        'Repeat the install loop with a second uncorrelated line.',
        'Build a 3-month reserve before scaling further.',
        'Document each install as a repeatable playbook.',
        'Refuse income lines that compromise your foundation.',
      ] },
    ],
  },
  fitness: {
    layers: [
      { name: 'Set the Honest Baseline', tasks: [
        'Record current weight, measurements, and 3 strength markers.',
        'Identify the one habit currently sabotaging progress.',
        'Pick a measurable 90-day target (not "get fitter").',
        'Choose a training style you\'ll actually do 4×/week.',
      ] },
      { name: 'Build Capability', tasks: [
        'Train 4×/week for 8 weeks. No skipped weeks.',
        'Hit a protein floor (≈1g per lb of target weight).',
        'Sleep 7+ hours, 6 nights of 7.',
        'Re-test the 3 strength markers at week 8.',
      ] },
      { name: 'Compound', tasks: [
        'Add one progression each month (weight, reps, distance).',
        'Track 1 lifestyle metric weekly (steps, recovery, etc.).',
        'Replace one bad food default with a good one each month.',
        'Re-test markers every 90 days. Adjust, don\'t restart.',
      ] },
    ],
  },
  property: {
    layers: [
      { name: 'Discover & Define', tasks: [
        'Define the brief: location, size, must-haves, deal-breakers.',
        'Set the all-in budget (purchase + closing + reserve).',
        'View 10 properties before you make any offer.',
        'Pick the 3 candidates that match the brief, not the mood.',
      ] },
      { name: 'Diligence & Funding', tasks: [
        'Get pre-approval for finance with two lenders.',
        'Commission an independent inspection on shortlisted property.',
        'Stress-test the cash-flow at +2% interest and 3 months vacancy.',
        'Negotiate on findings, not on emotion.',
      ] },
      { name: 'Acquire & Operate', tasks: [
        'Close with a clear written list of post-completion fixes.',
        'Set up the operating system (insurance, maintenance schedule, reserve).',
        'Document everything. Make it transferable to a manager.',
        'Review annually. Refinance or rebalance only on data.',
      ] },
    ],
  },
  craft: {
    layers: [
      { name: 'Define the Work', tasks: [
        'Write the one-sentence north star: who is this for, what does it do.',
        'Pick the format you can actually finish (length, medium, scope).',
        'Block 5 hours/week, same time each week.',
        'Identify your distinctive constraint and lean into it.',
      ] },
      { name: 'Make the Reps', tasks: [
        'Ship a small artefact every week for 8 weeks.',
        'Get honest feedback from 3 people who match the audience.',
        'Refine the voice, not the topic.',
        'Publish the first complete piece without apology.',
      ] },
      { name: 'Distribute & Compound', tasks: [
        'Pick one channel you respect. Show up weekly for 90 days.',
        'Refuse algorithm-bait. Let the work do the work.',
        'Invite the smallest audience that genuinely cares.',
        'Start the next piece before the last one finishes its run.',
      ] },
    ],
  },
  travel: {
    layers: [
      { name: 'Frame the Trip', tasks: [
        'Pick the 3 places that genuinely pull you (not the social-media list).',
        'Define the duration honestly, including the buffer days you\'ll need.',
        'Calculate the all-in cost (transport + stay + experiences + buffer).',
        'Pick the months that fit work and life, not just weather.',
      ] },
      { name: 'Build the Capability', tasks: [
        'Sort documents (passport, visas, insurance) 90 days out.',
        'Get the portable income or savings cleanly in place.',
        'Test gear before you leave (bag, devices, comfort items).',
        'Brief one person at home with an emergency plan.',
      ] },
      { name: 'Go & Compound', tasks: [
        'Travel light. Cut a third of the packing list before leaving.',
        'Plan the first 72 hours fully. Leave the rest open.',
        'Capture lessons weekly, not photos, lessons.',
        'Bring one habit home that the trip changed in you.',
      ] },
    ],
  },
  learning: {
    layers: [
      { name: 'Define the Skill', tasks: [
        'Write the proof-of-skill in one sentence: what artefact shows you\'ve learned it.',
        'Find one mentor, one resource, one peer.',
        'Block 30 focused minutes a day for 60 days.',
        'Decide the deadline, and the artefact you\'ll produce by then.',
      ] },
      { name: 'Practise with Feedback', tasks: [
        'Practise daily, not perfectly. Reps over polish.',
        'Submit work to feedback weekly. Edit ego, keep signal.',
        'Track one quantitative metric (words, problems solved, hours).',
        'Re-write the worst part of your output every week.',
      ] },
      { name: 'Ship & Compound', tasks: [
        'Publish or present the artefact at the deadline. No re-scoping.',
        'Teach what you\'ve learned to one person.',
        'Pick the next adjacent skill that compounds with this one.',
        'Audit at 90 days, what would you start with now?',
      ] },
    ],
  },
  generic: {
    layers: [
      { name: 'Discover & Frame', tasks: [
        'Write the goal in one sentence with a measurable end-state.',
        'List 3 successful examples of someone who already did it.',
        'Identify the single biggest unknown.',
        'Decide your honest 90-day target.',
      ] },
      { name: 'Build the Capability', tasks: [
        'Identify the one skill you must develop.',
        'Schedule 30 focused minutes daily for 30 days.',
        'Find one mentor / one resource / one peer.',
        'Ship one tangible artefact at the end of 30 days.',
      ] },
      { name: 'Execute & Compound', tasks: [
        'Set the next concrete milestone with a date attached.',
        'Eliminate the lowest-leverage activity to make space.',
        'Track weekly. Adjust monthly.',
        'Refuse all distractions that are not this goal.',
      ] },
    ],
  },
};

function buildActionPlan(prompt, topic, answers, goalId) {
  const plan = TOPIC_PLANS[topic] || TOPIC_PLANS.generic;
  const edgeAnswer = answers && answers[4] ? answers[4].trim() : null;
  return plan.layers.map((layer, i) => {
    const tasks = layer.tasks.map(text => createTask(text, goalId));
    if (i === 1 && edgeAnswer) {
      tasks.push(createTask(`Leverage your edge: ${truncate(edgeAnswer, 80)}`, goalId));
    }
    return {
      id: genId(),
      name: layer.name,
      level: i + 1,
      tasks,
    };
  });
}

// ─── SVG icons ────────────────────────────────────────────────────────────────

function HollowCircleIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 9 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="4.5" cy="4.5" r="3.2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function TriangleAlertIcon() {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 1.5L9 8.5H1L5 1.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <line x1="5" y1="4" x2="5" y2="6.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="5" cy="7.3" r="0.55" fill="currentColor" />
    </svg>
  );
}

// ─── Status meta ─────────────────────────────────────────────────────────────

const STATUS_META = {
  not_started:   { label: 'Not started',   icon: null,              color: '#555555', border: 'rgba(255,255,255,0.07)' },
  ongoing:       { label: 'Ongoing',       icon: HollowCircleIcon,  color: '#A0A0A0', border: 'rgba(160,160,160,0.22)' },
  high_priority: { label: 'High priority', icon: TriangleAlertIcon, color: '#ffffff', border: 'rgba(255,191,0,0.45)'   },
  overdue:       { label: 'Overdue',       icon: TriangleAlertIcon, color: '#ffffff', border: 'rgba(255,255,255,0.32)' },
  done:          { label: 'Done',          icon: null,              color: '#1D9E75', border: 'rgba(29,158,117,0.4)'   },
};

function TaskStatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full font-data uppercase"
      style={{
        fontSize: 9,
        letterSpacing: '0.10em',
        padding: '2px 7px',
        color: m.color,
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${m.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {Icon && <Icon />}
      {m.label}
    </span>
  );
}

// ─── ConvoFlow ────────────────────────────────────────────────────────────────

function ConvoFlow({ goal, onAnswer }) {
  const { convo, topic } = goal;
  const { step, answers } = convo;
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [step, customMode]);

  const currentQ = CONV_QUESTIONS[step];
  const suggestions = currentQ ? currentQ.s(topic) : [];

  const handleSuggestion = (text) => {
    setCustomMode(false);
    setCustomText('');
    onAnswer(text);
  };

  const handleCustomSubmit = () => {
    const t = customText.trim();
    if (!t) return;
    setCustomMode(false);
    setCustomText('');
    onAnswer(t);
  };

  // Build chat history from past answers
  const history = [];
  for (let i = 0; i < answers.length; i++) {
    history.push({ type: 'bot', text: CONV_QUESTIONS[i].q(topic) });
    history.push({ type: 'user', text: answers[i] });
  }

  return (
    <div className="panel p-5 space-y-4">
      {/* Chat history */}
      {history.length > 0 && (
        <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
          {history.map((msg, i) => (
            <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className="max-w-[78%] px-3 py-2 rounded-xl text-[13.5px] leading-[1.6]"
                style={
                  msg.type === 'user'
                    ? { background: 'color-mix(in srgb, var(--el) 15%, transparent)', border: '1px solid color-mix(in srgb, var(--el) 35%, transparent)', color: 'rgba(255,255,255,0.9)' }
                    : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.65)' }
                }
              >
                {msg.text}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Current question */}
      {step < 5 && currentQ && (
        <div>
          {/* Bot question bubble */}
          <div className="flex justify-start mb-4">
            <div
              className="max-w-[78%] px-3 py-2 rounded-xl text-[14px] font-head leading-[1.55]"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.9)' }}
            >
              {currentQ.q(topic)}
            </div>
          </div>

          {/* Suggestion buttons */}
          {!customMode && (
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestion(s)}
                  className="w-full text-left px-4 py-3 rounded-lg border text-[13.5px] leading-[1.55] transition-colors"
                  style={{
                    borderColor: 'rgba(255,255,255,0.09)',
                    background: 'rgba(255,255,255,0.03)',
                    color: 'rgba(255,255,255,0.82)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = 'color-mix(in srgb, var(--el) 45%, transparent)';
                    e.currentTarget.style.background = 'color-mix(in srgb, var(--el) 8%, transparent)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                  }}
                >
                  {s}
                </button>
              ))}
              <button
                onClick={() => setCustomMode(true)}
                className="btn btn-ghost w-full text-[13px]"
              >
                Something else…
              </button>
            </div>
          )}

          {/* Custom input */}
          {customMode && (
            <div className="space-y-2">
              <textarea
                className="input w-full"
                rows={3}
                placeholder="Type your own answer…"
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCustomSubmit(); }
                }}
                autoFocus
              />
              <div className="flex gap-2">
                <button className="btn btn-primary flex-1" onClick={handleCustomSubmit} disabled={!customText.trim()}>
                  <Check size={13} /> Submit
                </button>
                <button className="btn btn-ghost" onClick={() => { setCustomMode(false); setCustomText(''); }}>
                  Back
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}

// ─── ActionPlan ───────────────────────────────────────────────────────────────

function ActionPlan({ goal, onToggleTask, onStartTask }) {
  const { layers } = goal;

  const level1Done = layers[0] ? layers[0].tasks.every(t => t.done) : false;
  const level2Done = layers[1] ? layers[1].tasks.every(t => t.done) : false;

  return (
    <div className="space-y-4">
      {layers.map((layer, idx) => {
        const isLocked = (layer.level === 2 && !level1Done) || (layer.level === 3 && !level2Done);
        const prevLayer = layers[idx - 1];
        const remaining = prevLayer ? prevLayer.tasks.filter(t => !t.done).length : 0;
        const doneCount = layer.tasks.filter(t => t.done).length;
        const totalCount = layer.tasks.length;

        return (
          <div
            key={layer.id}
            className="panel p-5"
            style={isLocked ? { opacity: 0.55 } : {}}
          >
            {/* Layer header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <span
                  className="font-head text-[11px] px-2 py-1 rounded"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'color-mix(in srgb, var(--el) 80%, white)',
                  }}
                >
                  L{layer.level}
                </span>
                <div>
                  <div className="font-head text-[15px] text-white">{layer.name}</div>
                  <div className="eyebrow mt-0.5">Level {layer.level} · {doneCount}/{totalCount} done</div>
                </div>
              </div>
              {isLocked && (
                <div className="flex items-center gap-2 text-white/45">
                  <Lock size={13} />
                  <span className="text-[12px]">{remaining} task{remaining !== 1 ? 's' : ''} remaining to unlock</span>
                </div>
              )}
              {!isLocked && doneCount === totalCount && (
                <span className="eyebrow" style={{ color: '#1D9E75' }}>Complete</span>
              )}
            </div>

            {/* Tasks */}
            <div className="space-y-2">
              {layer.tasks.map(task => {
                const status = computeStatus(task);
                return (
                  <div
                    key={task.id}
                    className="px-3 py-2.5 rounded-md border flex items-start gap-3 transition-colors"
                    style={{
                      borderColor: task.done ? 'rgba(29,158,117,0.4)' : 'rgba(255,255,255,0.07)',
                      background: task.done ? 'rgba(29,158,117,0.06)' : 'rgba(255,255,255,0.02)',
                    }}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => !isLocked && onToggleTask(layer.id, task.id)}
                      className="h-4 w-4 rounded-full mt-0.5 flex items-center justify-center shrink-0 transition-colors"
                      style={{
                        background: task.done ? '#1D9E75' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${task.done ? '#1D9E75' : 'rgba(255,255,255,0.18)'}`,
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                      }}
                      disabled={isLocked}
                    >
                      {task.done && <Check size={10} className="text-[#050403]" strokeWidth={3} />}
                    </button>

                    {/* Text */}
                    <span
                      className="flex-1 text-[13px] leading-[1.55]"
                      style={{
                        color: task.done ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.85)',
                        textDecoration: task.done ? 'line-through' : 'none',
                      }}
                    >
                      {task.text}
                    </span>

                    {/* Start button + status badge */}
                    <div className="flex items-center gap-2 shrink-0">
                      {!task.startedAt && !task.done && !isLocked && (
                        <button
                          onClick={() => onStartTask(layer.id, task.id)}
                          className="btn text-[11px] flex items-center gap-1 py-1 px-2"
                          style={{ color: 'var(--el)', borderColor: 'color-mix(in srgb, var(--el) 40%, transparent)' }}
                        >
                          <Play size={10} /> Start
                        </button>
                      )}
                      <TaskStatusBadge status={status} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main LifeGoals component ─────────────────────────────────────────────────

function CoreLearningsPanel({ value, onChange, onComplete }) {
  return (
    <div className="panel-strong p-5">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl flex items-center justify-center border border-white/8 bg-white/4 shrink-0">
          <BookOpen size={17} style={{ color: 'var(--el)' }} />
        </div>
        <div className="flex-1">
          <div className="rail-left font-head text-[16px] text-white">Core learnings</div>
          <p className="mt-2 text-[13px] text-white/55 leading-[1.7]">
            Before this goal is shelved, record what worked, what blocked you, and what you would repeat. This becomes part of your Life OS memory.
          </p>
          <textarea
            className="input w-full mt-4"
            rows={5}
            placeholder="What did this goal teach you about how you solve problems?"
            value={value}
            onChange={e => onChange(e.target.value)}
            data-testid="goal-core-learnings"
          />
          <div className="mt-3 flex justify-end">
            <button
              className="btn btn-primary"
              onClick={onComplete}
              disabled={!value.trim()}
              data-testid="goal-complete-btn"
            >
              <CheckCircle2 size={14} /> Complete goal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompletedGoalView({ goal }) {
  const tasks = getGoalTasks(goal);
  const completedAt = goal.completedAt ? new Date(goal.completedAt) : null;

  return (
    <div className="space-y-4">
      <div className="panel-strong p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Completed goal</div>
            <div className="font-head text-[20px] md:text-[24px] text-white leading-[1.3]">{goal.prompt}</div>
            <div className="mt-2 text-[12px] text-white/42">
              {completedAt ? `Completed ${completedAt.toLocaleDateString()}` : 'Completed'}
            </div>
          </div>
          <span className="chip" style={{ color: '#1D9E75', borderColor: 'rgba(29,158,117,0.45)' }}>
            <CheckCircle2 size={12} /> Shelved
          </span>
        </div>
      </div>

      {goal.coreLearnings && (
        <div className="panel p-5">
          <div className="rail-left font-head text-[15px] text-white">Core learnings</div>
          <p className="mt-3 text-[13px] text-white/65 leading-[1.75] whitespace-pre-wrap">{goal.coreLearnings}</p>
        </div>
      )}

      <div className="panel p-5">
        <div className="rail-left font-head text-[15px] text-white mb-4">Completed steps</div>
        <div className="space-y-2">
          {tasks.map(task => (
            <div key={task.id} className="flex items-start gap-3 px-3 py-2.5 rounded-md border border-white/7 bg-white/[0.02]">
              <Check size={11} className="mt-1 shrink-0" style={{ color: '#1D9E75' }} />
              <div className="text-[13px] text-white/65 leading-[1.55]">{task.text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function LifeGoals() {
  const { state, addGoal, updateGoal, removeGoal } = useArc();
  const [draft, setDraft] = useState('');
  const visibleGoals = (state.goals || []).filter(g => !g.deleted);
  const [activeId, setActiveId] = useState(visibleGoals.find(g => !g.completed)?.id || visibleGoals[0]?.id || null);
  const [building, setBuilding] = useState(false);
  const [genPlan, setGenPlan] = useState(false);
  const [err, setErr] = useState('');
  const [coreLearnings, setCoreLearnings] = useState('');

  const activeGoals = visibleGoals.filter(g => !g.completed);
  const completedGoals = visibleGoals.filter(g => g.completed);
  const active = visibleGoals.find(g => g.id === activeId) || null;
  const activeReadyToComplete = active && !active.completed && isGoalReadyToComplete(active);

  // Auto-select first goal if nothing is active
  useEffect(() => {
    if (!activeId && activeGoals.length > 0) {
      setActiveId(activeGoals[0].id);
    }
  }, [activeId, activeGoals]);

  useEffect(() => {
    setCoreLearnings(active?.coreLearnings || '');
  }, [activeId, active?.coreLearnings]);

  // ── Generate a new goal ──

  const generateGoal = () => {
    if (!draft.trim()) return;
    setBuilding(true);
    setErr('');
    const text = draft.trim();
    const topic = inferTopic(text);
    const goal = {
      id: genId(),
      prompt: text,
      topic,
      createdAt: Date.now(),
      convo: { step: 0, answers: [] },
      layers: null,
      history: [{ type: 'created', at: nowIso() }],
    };
    addGoal(goal);
    setActiveId(goal.id);
    setDraft('');
    setBuilding(false);
  };

  // ── Handle conversation answer ──

  const submitAnswer = (text) => {
    if (!active) return;
    const currentStep = active.convo.step;
    const newAnswers = [...active.convo.answers, text];
    const newStep = currentStep + 1; // 5 means all answered
    updateGoal(active.id, g => ({
      ...g,
      convo: { step: newStep, answers: newAnswers },
    }));
  };

  // ── Generate action plan ──

  const generatePlan = async () => {
    if (!active) return;
    setGenPlan(true);
    setErr('');
    let layers;
    const aiAnswers = active.convo.answers.map((answer, i) => ({
      q: CONV_QUESTIONS[i]?.q(active.topic) || `Question ${i + 1}`,
      a: answer,
    }));
    try {
      const r = await fetch(`${API}/api/ai/goals/layers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ goal: active.prompt, answers: aiAnswers }),
      });
      if (!r.ok) throw new Error('layers failed');
      const data = await r.json();
      layers = (data.layers || []).map((l, i) => ({
        id: genId(),
        name: l.name || `Layer ${i + 1}`,
        level: i + 1,
        tasks: (l.tasks || []).map(t => createTask(typeof t === 'string' ? t : t.text, active.id)),
      }));
      if (layers.length === 0) throw new Error('empty');
    } catch {
      layers = buildActionPlan(active.prompt, active.topic, active.convo.answers, active.id);
      setErr('A local plan was generated and saved. Live planner can be retried later.');
    }
    updateGoal(active.id, g => addHistory({ ...g, layers }, 'plan_generated'));
    setGenPlan(false);
  };

  // ── Task interactions ──

  const toggleTask = (layerId, taskId) => {
    updateGoal(active.id, g => ({
      ...g,
      layers: g.layers.map(l =>
        l.id === layerId
          ? {
            ...l,
            tasks: l.tasks.map(t => {
              if (t.id !== taskId) return t;
              const done = !t.done;
              return addHistory({
                ...t,
                done,
                completedAt: done ? nowIso() : t.completedAt,
                reopenedAt: done ? t.reopenedAt : nowIso(),
              }, done ? 'completed' : 'reopened');
            })
          }
          : l
      ),
    }));
  };

  const startTask = (layerId, taskId) => {
    updateGoal(active.id, g => ({
      ...g,
      layers: g.layers.map(l =>
        l.id === layerId
          ? {
            ...l,
            tasks: l.tasks.map(t => t.id === taskId
              ? addHistory({ ...t, startedAt: t.startedAt || Date.now() }, 'started')
              : t)
          }
          : l
      ),
    }));
  };

  // ── Derived state ──

  const completeGoal = () => {
    if (!active || !coreLearnings.trim()) return;
    updateGoal(active.id, g => ({
      ...g,
      completed: true,
      completedAt: nowIso(),
      coreLearnings: coreLearnings.trim(),
      history: [...(g.history || []), { type: 'completed', at: nowIso(), coreLearnings: coreLearnings.trim() }],
    }));
  };

  const convoStep = active?.convo?.step ?? 0;
  const convoDone = convoStep >= 5;

  return (
    <div className="fade-up space-y-5">
      <div>
        <h1 className="font-head text-[28px] md:text-[34px] text-white/95">Life Goals</h1>
        <p className="text-white/60 text-[14px] mt-1 leading-[1.7] max-w-[760px]">
          Type any macro goal, personal or professional. Life OS turns it into compounding actions: motivation, sequence, proof, then the next level.
        </p>
      </div>

      <div className="grid md:grid-cols-[280px_1fr] gap-5">
        {/* ── Sidebar ── */}
        <div className="panel p-4 md:max-h-[820px] md:overflow-y-auto">
          <div className="rail-left font-head text-[14px] text-white/90 mb-3">Your Goals</div>
          {activeGoals.length === 0 && (
            <div className="text-white/45 text-[13px]">No active goals. Type one below.</div>
          )}
          <div className="space-y-2">
            {activeGoals.map(g => {
              const gStep = g.convo?.step ?? 0;
              const hasLayers = !!g.layers;
              const ready = isGoalReadyToComplete(g);
              return (
                <button
                  key={g.id}
                  onClick={() => setActiveId(g.id)}
                  className="w-full text-left px-3 py-3 rounded-lg border transition-colors"
                  style={{
                    borderColor: activeId === g.id ? 'color-mix(in srgb, var(--el) 50%, transparent)' : 'rgba(255,255,255,0.07)',
                    background: activeId === g.id ? 'color-mix(in srgb, var(--el) 8%, transparent)' : 'rgba(255,255,255,0.02)',
                  }}
                >
                  <div className="text-[13px] text-white/90 line-clamp-2">{g.prompt}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="eyebrow">{new Date(g.createdAt).toLocaleDateString()}</span>
                    {hasLayers && <span className="eyebrow" style={{ color: 'var(--el)' }}>Plan ready</span>}
                    {ready && <span className="eyebrow" style={{ color: '#1D9E75' }}>Ready</span>}
                    {!hasLayers && gStep > 0 && gStep < 5 && <span className="eyebrow">{gStep}/5 answers</span>}
                    {!hasLayers && gStep >= 5 && <span className="eyebrow">Awaiting plan</span>}
                  </div>
                </button>
              );
            })}
          </div>
          {completedGoals.length > 0 && (
            <div className="mt-6">
              <div className="rail-left font-head text-[14px] text-white/90 mb-3">Completed Goals</div>
              <div className="space-y-2">
                {completedGoals.map(g => (
                  <button
                    key={g.id}
                    onClick={() => setActiveId(g.id)}
                    className="w-full text-left px-3 py-3 rounded-lg border transition-colors"
                    style={{
                      borderColor: activeId === g.id ? 'rgba(29,158,117,0.45)' : 'rgba(255,255,255,0.07)',
                      background: activeId === g.id ? 'rgba(29,158,117,0.08)' : 'rgba(255,255,255,0.018)',
                    }}
                  >
                    <div className="text-[13px] text-white/80 line-clamp-2">{g.prompt}</div>
                    <div className="mt-1 eyebrow" style={{ color: '#1D9E75' }}>
                      Completed {g.completedAt ? new Date(g.completedAt).toLocaleDateString() : ''}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {visibleGoals.length > 0 && active && (
            <button
              className="btn w-full mt-3"
              onClick={() => {
                removeGoal(active.id);
                setActiveId(visibleGoals.find(g => g.id !== active.id)?.id || null);
              }}
            >
              <Trash2 size={13} /> Delete goal
            </button>
          )}
        </div>

        {/* ── Main area ── */}
        <div className="space-y-4">
          {/* Input row */}
          <div className="panel-strong p-4">
            <div className="flex flex-col md:flex-row gap-2">
              <input
                className="input flex-1"
                placeholder='e.g. "Buy a sailboat", "Start a business", "Run a marathon"'
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && generateGoal()}
                data-testid="goals-input"
              />
              <button
                className="btn btn-primary"
                onClick={generateGoal}
                disabled={!draft.trim() || building}
                data-testid="goals-begin-btn"
              >
                {building
                  ? <><Loader2 size={14} className="animate-spin" /> Starting…</>
                  : <><Zap size={14} /> Add goal</>}
              </button>
            </div>
            {err && (
              <div className="mt-2 text-[12px] text-amber-400/85" data-testid="goals-error">{err}</div>
            )}
          </div>

          {/* Empty state */}
          {!active && (
            <div className="panel p-10 text-center">
              <div className="chip chip-engine mx-auto inline-flex"><Sparkles size={12} /> Goal Engine</div>
              <div className="mt-4 font-head text-[22px] text-white">Type a goal to begin.</div>
              <div className="text-white/55 text-[14px] mt-2 max-w-[480px] mx-auto leading-[1.7]">
                Personal or professional. A five-question dialogue surfaces your motivation, then builds a sequential three-level action plan.
              </div>
            </div>
          )}

          {/* Active goal */}
          {active && (
            <>
              {active.completed && <CompletedGoalView goal={active} />}
              {!active.completed && (
                <>
              {/* Goal header */}
              <div className="panel-strong p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow mb-1">Goal</div>
                    <div className="font-head text-[20px] md:text-[22px] text-white leading-[1.3]">{active.prompt}</div>
                  </div>
                  <span className="chip chip-engine uppercase shrink-0">{active.topic}</span>
                </div>
              </div>

              {/* Conversation or plan */}
              {!convoDone && (
                <ConvoFlow goal={active} onAnswer={submitAnswer} />
              )}

              {convoDone && !active.layers && (
                <div className="panel p-8 text-center">
                  <div className="font-head text-[18px] text-white mb-2">Ready to build your plan</div>
                  <div className="text-white/55 text-[13.5px] mb-5 max-w-[440px] mx-auto leading-[1.65]">
                    Based on your five answers, we'll generate a sequential three-level action plan. Each level unlocks only when the previous is complete.
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={generatePlan}
                    disabled={genPlan}
                    data-testid="goals-plan-btn"
                  >
                    {genPlan
                      ? <><Loader2 size={14} className="animate-spin" /> Building plan…</>
                      : <><Sparkles size={14} /> Generate your plan</>}
                  </button>
                </div>
              )}

              {convoDone && active.layers && (
                <>
                  <ActionPlan
                    goal={active}
                    onToggleTask={toggleTask}
                    onStartTask={startTask}
                  />
                  {activeReadyToComplete ? (
                    <CoreLearningsPanel
                      value={coreLearnings}
                      onChange={setCoreLearnings}
                      onComplete={completeGoal}
                    />
                  ) : (
                    <div className="text-right">
                      <button
                        className="btn btn-ghost text-[12px]"
                        onClick={generatePlan}
                        disabled={genPlan}
                      >
                        {genPlan ? 'Regenerating...' : 'Regenerate plan'}
                      </button>
                    </div>
                  )}
                </>
              )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
